import React, { useEffect, useMemo, useState, useCallback } from 'react';

export default function Appointments({ role, user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/get_appointments.php', { credentials: 'include' });
      const data = await res.json();
      setRows(Array.isArray(data?.appointments) ? data.appointments : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await load();
    })();
    return () => { active = false; };
  }, [load]);

  async function updateStatus(id, nextStatus) {
    try {
      setBusyId(id);
      await fetch('/api/update_appointment.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: nextStatus })
      });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function deleteAppointment(id) {
    const confirm = window.confirm('Delete this appointment? This cannot be undone.');
    if (!confirm) return;
    try {
      setBusyId(id);
      await fetch('/api/delete_appointment.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function editAppointment(appt) {
    const date = window.prompt('New date (YYYY-MM-DD):', appt.date || '');
    if (date === null) return;
    const time = window.prompt('New time (HH:MM:SS):', appt.time || '');
    if (time === null) return;
    const service = window.prompt('Service:', appt.service || '');
    if (service === null) return;
    const notes = window.prompt('Notes:', appt.notes || '');
    if (notes === null) return;
    try {
      setBusyId(appt.id);
      await fetch('/api/update_appointment.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appt.id, date, time, service, notes })
      });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && (r.status || '').toLowerCase() !== statusFilter) return false;
      if (!q) return true;
      return [r.name, r.email, r.phone, r.service].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, query, statusFilter]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Appointments</h1>
        <div className="flex items-center gap-2">
          <input
            className="h-9 px-3 border rounded-md"
            placeholder="Search name/email/phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search appointments"
          />
          <select
            className="h-9 px-2 border rounded-md"
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Service</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-3" colSpan={8}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td className="p-3" colSpan={8}>No appointments found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <Td>{r.name}</Td>
                  <Td>{r.email}</Td>
                  <Td>{r.phone}</Td>
                  <Td>{r.date}</Td>
                  <Td>{r.time}</Td>
                  <Td>{r.service}</Td>
                  <Td>
                    <span className={`px-2 py-0.5 rounded text-xs ${statusColor(r.status)}`}>{r.status || 'pending'}</span>
                  </Td>
                  <Td className="text-right">
                    <button disabled={busyId===r.id} onClick={() => editAppointment(r)} className="text-blue-600 hover:underline mr-3 disabled:opacity-50" aria-label={`Edit appointment ${r.id}`}>Edit</button>
                    <button disabled={busyId===r.id} onClick={() => {
                      const next = window.prompt('Set status to: scheduled | completed | canceled | pending', String(r.status||'pending'));
                      if (!next) return; updateStatus(r.id, next.toLowerCase());
                    }} className="text-gray-600 hover:underline mr-3 disabled:opacity-50" aria-label={`Update status for ${r.id}`}>Status</button>
                    <button disabled={busyId===r.id} onClick={() => deleteAppointment(r.id)} className="text-red-600 hover:underline disabled:opacity-50" aria-label={`Delete appointment ${r.id}`}>Delete</button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children, className = '' }) {
  return <th className={`p-3 font-medium text-left ${className}`}>{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}

function statusColor(status = 'pending') {
  const s = String(status).toLowerCase();
  if (s === 'completed') return 'bg-green-100 text-green-700';
  if (s === 'canceled') return 'bg-red-100 text-red-700';
  if (s === 'scheduled') return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
}
